import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from 'sonner';
import { CheckCircle, Sparkles, Globe, Image, FileText, BarChart3, Users, Mail } from 'lucide-react';

const Signup = () => {
  const navigate = useNavigate();
  const { signup, user } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    language_preference: 'EN'
  });
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  if (user) {
    navigate('/dashboard');
    return null;
  }

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error(language === 'ES' ? 'Por favor completa todos los campos requeridos' : 'Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error(language === 'ES' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error(language === 'ES' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await signup(
        formData.name,
        formData.email,
        formData.password,
        formData.phone,
        formData.language_preference,
        'Pro'  // Single plan
      );
      toast.success(language === 'ES' ? '¡Bienvenido a ListingPro AI! Tu prueba de 7 días ha comenzado.' : 'Welcome to ListingPro AI! Your 7-day trial has started.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Signup error:', error);
      const message = error.response?.data?.detail || (language === 'ES' ? 'Error al crear la cuenta' : 'Failed to create account');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary/30 flex flex-col lg:flex-row">
      {/* Left side - Features showcase (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-900 to-gray-800 p-12 flex-col justify-center">
        <div className="max-w-md mx-auto">
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Everything you need to market your listings
          </h2>
          <p className="text-white/60 mb-8">
            Join thousands of agents using AI to create professional marketing in minutes.
          </p>
          
          <div className="space-y-4">
            {[
              { icon: Image, title: 'AI Photo Enhancement', desc: 'One-click photo improvements' },
              { icon: FileText, title: 'MLS Remarks Generator', desc: 'Professional descriptions in seconds' },
              { icon: Mail, title: 'Email & Social Copy', desc: 'Ready-to-post marketing content' },
              { icon: BarChart3, title: 'Market Data Insights', desc: 'Real-time market trends & updates' },
              { icon: Users, title: 'Contact Management', desc: 'Manage clients & import from CSV' }
            ].map((feature, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <feature.icon className="text-blue-400" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-white/60">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10">
            <p className="text-white/80 text-sm italic">
              "ListingPro AI saved me hours on every listing. The market update feature is a game-changer for staying in touch with clients."
            </p>
            <p className="text-white/50 text-sm mt-2">— Miami Real Estate Agent</p>
          </div>
        </div>
      </div>
      
      {/* Right side - Signup form */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-6 flex items-center justify-between">
          <Link to="/">
            <Logo />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLanguage(language === 'EN' ? 'ES' : 'EN')}
            className="flex items-center gap-2"
          >
            <Globe size={18} />
            {language === 'EN' ? 'ES' : 'EN'}
          </Button>
        </div>

      {/* Signup Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-lg border-border/40 shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-heading">{t('signup_title')}</CardTitle>
            <CardDescription>{t('signup_subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Google Sign Up Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full mb-6 py-6 rounded-full flex items-center justify-center gap-3 hover:bg-secondary/50"
              onClick={handleGoogleSignup}
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {language === 'ES' ? 'Continuar con Google' : 'Continue with Google'}
            </Button>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  {language === 'ES' ? 'O regístrate con email' : 'Or sign up with email'}
                </span>
              </div>
            </div>

            {/* Plan Info Banner */}
            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20 mb-6">
              <Sparkles className="text-blue-500 flex-shrink-0" size={24} />
              <div>
                <p className="font-semibold text-foreground">$20 {language === 'ES' ? 'pago único' : 'one-time'} • {language === 'ES' ? 'Acceso de por vida' : 'Lifetime access'}</p>
                <p className="text-sm text-muted-foreground">{t('feature_listings')} • {language === 'ES' ? 'Todas las funciones incluidas' : 'All features included'}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('signup_name')} *</Label>
                  <Input
                    id="name"
                    placeholder={language === 'ES' ? 'Juan Pérez' : 'John Doe'}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="signup-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('signup_phone')}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="signup-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('signup_email')} *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="signup-email-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">{t('signup_password')} *</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="signup-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('signup_confirm')} *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    data-testid="signup-confirm-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">{t('signup_language')}</Label>
                <Select 
                  value={formData.language_preference}
                  onValueChange={(value) => setFormData({ ...formData, language_preference: value })}
                >
                  <SelectTrigger data-testid="signup-language-select">
                    <SelectValue placeholder={language === 'ES' ? 'Selecciona idioma' : 'Select language'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EN">{t('english')}</SelectItem>
                    <SelectItem value="ES">{t('spanish')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-start gap-2 text-sm text-muted-foreground bg-secondary/50 p-3 rounded-lg">
                <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                <span>{t('signup_trial_note')}</span>
              </div>

              <Button 
                type="submit" 
                className="w-full rounded-full py-6 bg-accent hover:bg-accent/90" 
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? (language === 'ES' ? 'Creando cuenta...' : 'Creating account...') : t('signup_button')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('signup_have_account')}{' '}
                <Link 
                  to="/login" 
                  className="text-primary font-medium hover:underline"
                  data-testid="signup-login-link"
                >
                  {t('signup_login_link')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default Signup;
